<template>
  <div
    class="input-group p-relative text-medium text-b14 d-flex items-center"
    @click="onFocus"
    :class="{
      focused: focused,
      'on-fill': filled,
      dirtied: dirty,
      'has-error': !!error,
      'disabled bg-cont-dim color-on-cont-muted': disabled,
    }"
  >
    <div class="d-flex w-100" :class="{ 'items-center': type !== 'textarea' }">
      <div class="input-group__label p-absolute text-medium text-b14 color-on-cont-dim">
        {{ placeholder }}
      </div>
      <div class="input-group__input">
        <input
          v-if="!type || type !== 'textarea'"
          ref="input"
          class="text-input text-b14 text-medium w-100 color-on-cont-bright"
          :maxlength="computedMaxLength"
          :value="modelValue"
          :inputmode="computedInputMode"
          :disabled="disabled"
          :name="name"
          :id="name"
          autocomplete="on"
          @focus="onFocus"
          @blur="onBlur"
          @focusout="onFocusOut"
          @input="updateValue"
        />
        <textarea
          v-else
          :value="modelValue"
          :style="{ height: `${height || 100}px` }"
          :name="name"
          :id="name"
          class="text-input text-b14 w-100 text-medium color-on-cont-bright"
          autocomplete="on"
          @focus="onFocus"
          @blur="onBlur"
          @focusout="onFocusOut"
          @input="updateValue"
          ref="input"
        ></textarea>
      </div>
      <div v-if="!!error">
        <Tooltip show="hover" :text="error" tool-tip-class-name="tooltip-top-0">
          <span>
            <Icon name="error"></Icon>
          </span>
        </Tooltip>
      </div>
      <div class="input-group__details"></div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, inject, watch, nextTick, getCurrentInstance, onMounted, onBeforeUnmount, onUpdated, computed } from 'vue';
import Icon from '@/components/base/icon/index.vue';
import Tooltip from '@/components/tooltip/index.vue';
import type { BaseTextFieldProps, InjectFormType, ValidFunction } from '@/types';
import { extractOnlyNumbers, toEnNumber } from '@/utils';
import { emailValidator, nameValidator, phoneNumberValidator, requiredValidator } from '@/utils/inputValidations';

const props = defineProps<BaseTextFieldProps>();

const emit = defineEmits(['update:modelValue']);

const activeReversAnimaton = ref<boolean>(false);
const focused = ref<boolean>(false);
const dirty = ref<boolean>(false);
const error = ref<string>('');
const input: any = ref(null);
const value = ref<string>('');
const form = inject<InjectFormType>('form');
const uid = getCurrentInstance()?.uid as number;
const isMobile = ref<boolean>(props.rules?.[1] === 'phone');
const computedInputMode = computed(() => (isMobile.value ? 'tel' : 'text'));
const computedMaxLength = computed(() => (isMobile.value ? 11 : undefined));

const filled = computed(() => {
  return focused.value || props.modelValue;
});

onMounted(() => {
  if (props.err) {
    error.value = props.err;
  }
  if (props.modelValue) {
    dirty.value = true;
  }
  form?.bind({ validate, uid });
});

onUpdated(() => {
  if (props.err !== undefined) {
    error.value = props.err;
  }
});

onBeforeUnmount(() => {
  form?.unbind(uid);
});

watch(value, (newValue: string) => {
  if (newValue && !!error.value) {
    validate();
    form?.validate();
  }
});

function updateValue(event: Event) {
  const target = event.target as HTMLInputElement;
  let inputValue = target.value;

  // If input mode is tel (numeric keyboard), only allow numbers
  if (isMobile.value) {
    inputValue = extractOnlyNumbers(inputValue);
    target.value = inputValue;
  }

  value.value = inputValue;
  emit('update:modelValue', inputValue);
}

async function onFocus() {
  activeReversAnimaton.value = false;
  focused.value = true;
  dirty.value = true;
  await nextTick();
  input.value.focus();
}

function onFocusOut() {
  activeReversAnimaton.value = true;
  focused.value = false;
  if (error.value) {
    validate();
    form?.validate();
  }
}

function onBlur() {
  if (!props.modelValue) {
    focused.value = false;
  }
}

function validate(): ValidFunction {
  let valid = true;

  if (!props.rules) return { valid, error: '' } as any;

  for (const rule of props.rules) {
    valid = checkValidation(rule);
    if (!valid) break;
  }

  return { valid, error: error.value } as any;
}

function checkValidation(rule: string | { min: number }): boolean {
  if (rule === 'required') {
    const isValid = required(props.modelValue);
    if (!isValid) return false;
  } else if (rule === 'email') {
    const isValid = emailValidation(props.modelValue);
    if (!isValid) return false;
  } else if (rule === 'mobile' || rule === 'phone') {
    const isValid = mobileValidation(props.modelValue);
    if (!isValid) return false;
  } else if (rule === 'name') {
    const isValid = nameValidation(props.modelValue);
    if (!isValid) return false;
  }
  return true;
}

function required(value: string): boolean {
  const placeholder = props.placeholder || '';
  const result = requiredValidator(value, placeholder);
  error.value = result.error;
  return result.isValid;
}

function mobileValidation(value: string) {
  if (!value) return true;

  const result = phoneNumberValidator(toEnNumber(value));
  error.value = result.error;
  return result.isValid;
}

function emailValidation(value: string): boolean {
  if (!value) return true;
  const result = emailValidator(toEnNumber(value));
  error.value = result.error;
  return result.isValid;
}

function nameValidation(value: string): boolean {
  if (!value) return true;
  const result = nameValidator(value);
  error.value = result.error;
  return result.isValid;
}
</script>

<style lang="scss" scoped>
@import url('./index.scss');
</style>
